import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, ChevronDown, ChevronRight, Copy, Check, Star, Home, Sun, Moon as MoonIcon, Grid3X3, BarChart2, Compass, Eye, Layers, Calendar, Activity, Target, Sparkles, Globe, Zap } from 'lucide-react';
import { ChartToggle } from '../components/charts';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

const MandeepKernelPage = () => {
  const [kernelData, setKernelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedField, setCopiedField] = useState(null);
  const [globalChartStyle, setGlobalChartStyle] = useState('north');
  const [showDegrees, setShowDegrees] = useState(false);

  useEffect(() => {
    fetchKernel();
  }, []);

  const fetchKernel = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/mandeep-kernel/parashara-complete`);
      if (!response.ok) throw new Error('Failed to fetch kernel');
      const data = await response.json();
      setKernelData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadKernel = () => {
    if (!kernelData) return;
    const blob = new Blob([JSON.stringify(kernelData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mandeep_singh_kernel_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(JSON.stringify(text, null, 2));
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getPlanetColor = (planet) => {
    const colors = {
      Sun: 'text-orange-400', Moon: 'text-gray-300', Mars: 'text-red-400',
      Mercury: 'text-green-400', Jupiter: 'text-yellow-400', Venus: 'text-pink-400',
      Saturn: 'text-blue-400', Rahu: 'text-purple-400', Ketu: 'text-amber-600'
    };
    return colors[planet] || 'text-white';
  };

  // Prepare chart data from kernel
  const getChartData = (chartKey = 'D1_Rashi') => {
    if (!kernelData) return { planets: {}, houseSignMap: {}, ascendantSign: 'Leo' };
    
    const planets = {};
    const houseSignMap = {};
    
    // Get house sign mapping
    if (kernelData.house_signs) {
      Object.entries(kernelData.house_signs).forEach(([house, data]) => {
        houseSignMap[parseInt(house)] = data.sign;
      });
    }

    // Get ascendant sign
    const ascendantSign = houseSignMap[1] || 'Leo';

    // Get planets from main planets data or divisional chart
    if (chartKey === 'D1_Rashi' && kernelData.planets) {
      Object.entries(kernelData.planets).forEach(([planet, data]) => {
        planets[planet] = {
          house: data.house || kernelData.planet_houses?.[planet],
          degree: data.degree,
          sign: data.sign,
          retrograde: data.retrograde
        };
      });
    } else if (kernelData.divisional_charts?.[chartKey]) {
      const chartData = kernelData.divisional_charts[chartKey];
      Object.entries(chartData).forEach(([planet, data]) => {
        if (planet !== 'Ascendant') {
          planets[planet] = {
            house: data.house,
            degree: data.degree || 0,
            sign: data.sign,
            retrograde: data.retrograde || false
          };
        }
      });
    }

    return { planets, houseSignMap, ascendantSign };
  };

  // Tabs configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'charts', label: 'Charts (D1-D60)', icon: Grid3X3 },
    { id: 'planets', label: 'Planets', icon: Sun },
    { id: 'houses', label: 'Houses', icon: Home },
    { id: 'dasha', label: 'Dasha Systems', icon: Calendar },
    { id: 'shadbala', label: 'Shadbala', icon: BarChart2 },
    { id: 'ashtakavarga', label: 'Ashtakavarga', icon: Grid3X3 },
    { id: 'jaimini', label: 'Jaimini', icon: Compass },
    { id: 'kp', label: 'KP System', icon: Target },
    { id: 'yogas', label: 'Yogas', icon: Star },
    { id: 'upagrahas', label: 'Upagrahas', icon: Sparkles },
    { id: 'varshaphala', label: 'Varshaphala', icon: Calendar },
    { id: 'special', label: 'Special Lagnas', icon: Globe },
    { id: 'raw', label: 'Raw JSON', icon: Layers }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-amber-400 text-lg">Generating Complete Parashara Kernel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Error: {error}</p>
          <button onClick={fetchKernel} className="px-6 py-3 bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-all flex items-center gap-2 mx-auto">
            <RefreshCw className="w-5 h-5" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const chartData = getChartData();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-gray-900/95 backdrop-blur border-b border-amber-500/30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-amber-400">
                {kernelData?.name || 'Mandeep Singh'} - Parashara Kernel
              </h1>
              <p className="text-gray-400 text-sm">
                {kernelData?.birth_particulars?.date} | {kernelData?.birth_particulars?.time} | {kernelData?.birth_particulars?.place}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDegrees(!showDegrees)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${showDegrees ? 'bg-amber-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
              >
                {showDegrees ? 'Hide Degrees' : 'Show Degrees'}
              </button>
              <button
                onClick={downloadKernel}
                data-testid="download-json-btn"
                className="px-4 py-2 bg-amber-500 text-black rounded-lg hover:bg-amber-400 transition-all flex items-center gap-2 font-semibold"
              >
                <Download className="w-4 h-4" /> Download JSON
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="sticky top-20 z-40 bg-gray-900/90 backdrop-blur border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex overflow-x-auto py-2 gap-1 scrollbar-hide">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-amber-500 text-black'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <OverviewTab kernelData={kernelData} chartData={chartData} showDegrees={showDegrees} getPlanetColor={getPlanetColor} />
        )}

        {/* Charts Tab - D1 to D60 */}
        {activeTab === 'charts' && (
          <ChartsTab kernelData={kernelData} getChartData={getChartData} showDegrees={showDegrees} />
        )}

        {/* Planets Tab */}
        {activeTab === 'planets' && (
          <PlanetsTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* Houses Tab */}
        {activeTab === 'houses' && (
          <HousesTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* Dasha Tab */}
        {activeTab === 'dasha' && (
          <DashaTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* Shadbala Tab */}
        {activeTab === 'shadbala' && (
          <ShadbalaTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* Ashtakavarga Tab */}
        {activeTab === 'ashtakavarga' && (
          <AshtakavargaTab kernelData={kernelData} />
        )}

        {/* Jaimini Tab */}
        {activeTab === 'jaimini' && (
          <JaiminiTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* KP System Tab */}
        {activeTab === 'kp' && (
          <KPSystemTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* Yogas Tab */}
        {activeTab === 'yogas' && (
          <YogasTab kernelData={kernelData} getPlanetColor={getPlanetColor} />
        )}

        {/* Upagrahas Tab */}
        {activeTab === 'upagrahas' && (
          <UpagrahasTab kernelData={kernelData} />
        )}

        {/* Varshaphala Tab */}
        {activeTab === 'varshaphala' && (
          <VarshaphalaTab kernelData={kernelData} getChartData={getChartData} showDegrees={showDegrees} getPlanetColor={getPlanetColor} />
        )}

        {/* Special Lagnas Tab */}
        {activeTab === 'special' && (
          <SpecialLagnasTab kernelData={kernelData} />
        )}

        {/* Raw JSON Tab */}
        {activeTab === 'raw' && (
          <RawJSONTab kernelData={kernelData} copyToClipboard={copyToClipboard} copiedField={copiedField} />
        )}
      </div>
    </div>
  );
};

// ========== OVERVIEW TAB ==========
const OverviewTab = ({ kernelData, chartData, showDegrees, getPlanetColor }) => {
  return (
    <div className="space-y-6">
      {/* Birth Details & Main Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Birth Info */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Birth Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Date</p>
              <p className="text-white font-medium">{kernelData?.birth_particulars?.date}</p>
            </div>
            <div>
              <p className="text-gray-400">Time</p>
              <p className="text-white font-medium">{kernelData?.birth_particulars?.time}</p>
            </div>
            <div>
              <p className="text-gray-400">Place</p>
              <p className="text-white font-medium">{kernelData?.birth_particulars?.place}</p>
            </div>
            <div>
              <p className="text-gray-400">Coordinates</p>
              <p className="text-white font-medium">{kernelData?.birth_particulars?.latitude}°N, {kernelData?.birth_particulars?.longitude}°E</p>
            </div>
            <div>
              <p className="text-gray-400">Timezone</p>
              <p className="text-white font-medium">{kernelData?.birth_particulars?.timezone}</p>
            </div>
            <div>
              <p className="text-gray-400">Ayanamsa</p>
              <p className="text-white font-medium">{kernelData?.astronomical_data?.ayanamsa_degrees?.toFixed?.(4) || kernelData?.astronomical_data?.ayanamsa || 'N/A'}° (Lahiri)</p>
            </div>
          </div>
        </div>

        {/* Main Rashi Chart */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4 text-center">Rashi Chart (D1)</h3>
          <div className="flex justify-center">
            <ChartToggle
              planets={chartData.planets}
              houseSignMap={chartData.houseSignMap}
              ascendantSign={chartData.ascendantSign}
              size={280}
              showDegrees={showDegrees}
            />
          </div>
        </div>
      </div>

      {/* Panchang & Hindu Calendar */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Panchang */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Panchang</h3>
          <div className="space-y-3 text-sm">
            {kernelData?.panchang && Object.entries(kernelData.panchang).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="text-white">{typeof value === 'object' ? value.name || JSON.stringify(value) : value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Avakhada Chakra */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Avakhada Chakra</h3>
          <div className="space-y-3 text-sm">
            {kernelData?.avakhada_chakra && Object.entries(kernelData.avakhada_chakra).slice(0, 8).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Sunrise/Sunset */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Sunrise & Sunset</h3>
          <div className="space-y-3 text-sm">
            {kernelData?.sunrise_sunset && Object.entries(kernelData.sunrise_sunset).map(([key, value]) => (
              <div key={key} className="flex justify-between">
                <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="text-white">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Planet Summary */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Planet Positions Summary</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left py-2 px-3">Planet</th>
                <th className="text-left py-2 px-3">Sign</th>
                <th className="text-left py-2 px-3">Degree</th>
                <th className="text-left py-2 px-3">Nakshatra</th>
                <th className="text-left py-2 px-3">House</th>
                <th className="text-left py-2 px-3">Dignity</th>
              </tr>
            </thead>
            <tbody>
              {kernelData?.planets && Object.entries(kernelData.planets).map(([planet, data]) => (
                <tr key={planet} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className={`py-3 px-3 font-semibold ${getPlanetColor(planet)}`}>{planet}</td>
                  <td className="py-3 px-3 text-white">{data.sign}</td>
                  <td className="py-3 px-3 text-gray-300 font-mono">{data.degree?.toFixed(2)}°</td>
                  <td className="py-3 px-3 text-purple-300">{data.nakshatra} ({data.pada})</td>
                  <td className="py-3 px-3 text-amber-400">H{data.house}</td>
                  <td className="py-3 px-3 text-cyan-300">{data.dignity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ========== CHARTS TAB (D1-D60) ==========
const ChartsTab = ({ kernelData, getChartData, showDegrees }) => {
  const [selectedChart, setSelectedChart] = useState('D1_Rashi');
  
  const divisionalCharts = kernelData?.divisional_charts ? Object.keys(kernelData.divisional_charts) : [];
  
  // Group charts by category
  const mainCharts = divisionalCharts.filter(c => ['D1_Rashi', 'D9_Navamsa', 'D2_Hora', 'D3_Drekkana', 'D4_Chaturthamsa'].includes(c));
  const shodashaVargas = divisionalCharts.filter(c => {
    const num = parseInt(c.split('_')[0].replace('D', ''));
    return num <= 16 && !mainCharts.includes(c);
  });
  const otherCharts = divisionalCharts.filter(c => !mainCharts.includes(c) && !shodashaVargas.includes(c));

  const getChartDataForKey = (chartKey) => {
    if (!kernelData?.divisional_charts?.[chartKey]) return { planets: {}, houseSignMap: {}, ascendantSign: 'Leo' };
    
    const chartPlanets = kernelData.divisional_charts[chartKey];
    const planets = {};
    let ascendantSign = 'Leo';

    Object.entries(chartPlanets).forEach(([planet, data]) => {
      if (planet === 'Ascendant') {
        ascendantSign = data.sign || 'Leo';
      } else {
        planets[planet] = {
          sign: data.sign,
          degree: data.degree || 0,
          house: data.house,
          retrograde: data.retrograde || false
        };
      }
    });

    // Build house sign map from ascendant
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    const ascIndex = signs.indexOf(ascendantSign);
    const houseSignMap = {};
    for (let i = 0; i < 12; i++) {
      houseSignMap[i + 1] = signs[(ascIndex + i) % 12];
    }

    return { planets, houseSignMap, ascendantSign };
  };

  const currentChartData = getChartDataForKey(selectedChart);

  return (
    <div className="space-y-6">
      {/* Chart Selector */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Select Divisional Chart</h3>
        
        {/* Main Charts */}
        <div className="mb-4">
          <p className="text-gray-400 text-sm mb-2">Main Charts</p>
          <div className="flex flex-wrap gap-2">
            {mainCharts.map(chart => (
              <button
                key={chart}
                onClick={() => setSelectedChart(chart)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  selectedChart === chart ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {chart.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Shodasha Vargas */}
        <div className="mb-4">
          <p className="text-gray-400 text-sm mb-2">Shodasha Vargas (D5-D16)</p>
          <div className="flex flex-wrap gap-2">
            {shodashaVargas.map(chart => (
              <button
                key={chart}
                onClick={() => setSelectedChart(chart)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  selectedChart === chart ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {chart.split('_')[0]}
              </button>
            ))}
          </div>
        </div>

        {/* Higher Vargas */}
        <div>
          <p className="text-gray-400 text-sm mb-2">Higher Vargas (D17-D60)</p>
          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {otherCharts.map(chart => (
              <button
                key={chart}
                onClick={() => setSelectedChart(chart)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${
                  selectedChart === chart ? 'bg-amber-500 text-black' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {chart.split('_')[0]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Selected Chart Display */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Chart Visual */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4 text-center">
            {selectedChart.replace('_', ' ')}
          </h3>
          <div className="flex justify-center">
            <ChartToggle
              planets={currentChartData.planets}
              houseSignMap={currentChartData.houseSignMap}
              ascendantSign={currentChartData.ascendantSign}
              size={300}
              showDegrees={showDegrees}
            />
          </div>
        </div>

        {/* Chart Table */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Planet Positions - {selectedChart.split('_')[0]}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-2">Planet</th>
                  <th className="text-left py-2 px-2">Sign</th>
                  <th className="text-left py-2 px-2">Degree</th>
                </tr>
              </thead>
              <tbody>
                {kernelData?.divisional_charts?.[selectedChart] && 
                  Object.entries(kernelData.divisional_charts[selectedChart]).map(([planet, data]) => (
                    <tr key={planet} className="border-b border-gray-800">
                      <td className="py-2 px-2 text-amber-400">{planet}</td>
                      <td className="py-2 px-2 text-white">{data.sign}</td>
                      <td className="py-2 px-2 text-gray-300 font-mono">
                        {data.degree != null ? (typeof data.degree === 'number' ? `${data.degree.toFixed(2)}°` : data.degree) : '-'}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== PLANETS TAB ==========
const PlanetsTab = ({ kernelData, getPlanetColor }) => {
  return (
    <div className="space-y-6">
      {/* Detailed Planet Table */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Complete Planet Data</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 border-b border-gray-700">
                <th className="text-left py-2 px-2">Planet</th>
                <th className="text-left py-2 px-2">Sign</th>
                <th className="text-left py-2 px-2">Longitude</th>
                <th className="text-left py-2 px-2">Nakshatra</th>
                <th className="text-left py-2 px-2">Pada</th>
                <th className="text-left py-2 px-2">House</th>
                <th className="text-left py-2 px-2">Dignity</th>
                <th className="text-left py-2 px-2">Speed</th>
                <th className="text-left py-2 px-2">Retro</th>
                <th className="text-left py-2 px-2">Shadbala</th>
              </tr>
            </thead>
            <tbody>
              {kernelData?.planets && Object.entries(kernelData.planets).map(([planet, data]) => (
                <tr key={planet} className="border-b border-gray-800 hover:bg-gray-800/50">
                  <td className={`py-3 px-2 font-semibold ${getPlanetColor(planet)}`}>{planet}</td>
                  <td className="py-3 px-2 text-white">{data.sign}</td>
                  <td className="py-3 px-2 text-gray-300 font-mono">{data.longitude?.toFixed(4)}°</td>
                  <td className="py-3 px-2 text-purple-300">{data.nakshatra}</td>
                  <td className="py-3 px-2 text-cyan-300">{data.pada}</td>
                  <td className="py-3 px-2 text-amber-400">H{data.house}</td>
                  <td className="py-3 px-2 text-green-400">{data.dignity_full || data.dignity}</td>
                  <td className="py-3 px-2 text-gray-400 font-mono">{data.speed?.toFixed(4)}</td>
                  <td className="py-3 px-2">{data.retrograde ? <span className="text-red-400">R</span> : '-'}</td>
                  <td className="py-3 px-2 text-yellow-400">{data.shadbala?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Graha Avasthas */}
      {kernelData?.graha_avasthas && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Graha Avasthas (Planetary States)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(kernelData.graha_avasthas).map(([planet, avasthas]) => (
              <div key={planet} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <h4 className={`font-semibold mb-2 ${getPlanetColor(planet)}`}>{planet}</h4>
                <div className="space-y-1 text-xs">
                  {Object.entries(avasthas).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                      <span className="text-white">{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== HOUSES TAB ==========
const HousesTab = ({ kernelData, getPlanetColor }) => {
  return (
    <div className="space-y-6">
      {/* House Signs */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">House Signs & Lords</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {kernelData?.house_signs && Object.entries(kernelData.house_signs).map(([house, data]) => (
            <div key={house} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
              <div className="flex justify-between items-center mb-2">
                <span className="text-amber-400 font-bold">House {house}</span>
                <span className="text-white">{data.sign}</span>
              </div>
              <div className="text-sm">
                <span className="text-gray-400">Lord: </span>
                <span className={getPlanetColor(data.lord)}>{data.lord}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Houses with detailed info */}
      {kernelData?.houses && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Detailed House Analysis</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(kernelData.houses).map(([house, data]) => (
              <div key={house} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-amber-400 font-bold">House {house}</span>
                  <span className="text-white">{data.sign}</span>
                </div>
                {data.signification && (
                  <p className="text-gray-400 text-xs mb-2">{data.signification}</p>
                )}
                <div className="space-y-1 text-sm">
                  <div>
                    <span className="text-gray-500">Lord: </span>
                    <span className={getPlanetColor(data.lord)}>{data.lord}</span>
                    {data.lord_placed_in && (
                      <span className="text-gray-500"> in H{data.lord_placed_in}</span>
                    )}
                  </div>
                  {data.planets_in_house?.length > 0 && (
                    <div>
                      <span className="text-gray-500">Planets: </span>
                      {data.planets_in_house.map((p, i) => (
                        <span key={p} className={getPlanetColor(p)}>
                          {p}{i < data.planets_in_house.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bhava Chalit */}
      {kernelData?.bhava_chalit && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Bhava Chalit</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-3">Planet</th>
                  <th className="text-left py-2 px-3">Rashi House</th>
                  <th className="text-left py-2 px-3">Bhava House</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kernelData.bhava_chalit).map(([planet, data]) => (
                  <tr key={planet} className="border-b border-gray-800">
                    <td className={`py-2 px-3 font-semibold ${getPlanetColor(planet)}`}>{planet}</td>
                    <td className="py-2 px-3 text-white">H{data.rashi_house || data.house}</td>
                    <td className="py-2 px-3 text-cyan-300">H{data.bhava_house || data.bhava}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== DASHA TAB ==========
const DashaTab = ({ kernelData, getPlanetColor }) => {
  const [selectedDasha, setSelectedDasha] = useState('vimshottari');
  const [expandedPeriods, setExpandedPeriods] = useState({});

  // Get all available dasha systems from all_dashas
  const dashaSystems = [];
  if (kernelData?.all_dashas) {
    Object.keys(kernelData.all_dashas).forEach(key => {
      const name = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      dashaSystems.push({ key, name });
    });
  }
  // Fallback to main dasha if no all_dashas
  if (dashaSystems.length === 0 && kernelData?.dasha?.mahadashas) {
    dashaSystems.push({ key: 'main', name: 'Vimshottari' });
  }

  const togglePeriod = (path) => {
    setExpandedPeriods(prev => ({ ...prev, [path]: !prev[path] }));
  };

  // Recursive Dasha Period Component - handles nested levels
  const DashaPeriod = ({ period, level = 0, path = '' }) => {
    const lordName = period.lord || period.dasha_lord || period.sign || 'Unknown';
    const currentPath = path ? `${path}-${lordName}-${level}` : `${lordName}-${level}`;
    const isExpanded = expandedPeriods[currentPath];
    
    // Check for sub-periods at any level
    const subPeriods = period.antardashas || period.pratyantardashas || period.sookshmas || period.pranas || period.sub_periods || [];
    const hasSubPeriods = subPeriods.length > 0;
    
    const levelColors = [
      'border-amber-500', 'border-blue-500', 'border-green-500', 
      'border-purple-500', 'border-pink-500'
    ];
    const levelBgs = [
      'bg-amber-500/10', 'bg-blue-500/10', 'bg-green-500/10',
      'bg-purple-500/10', 'bg-pink-500/10'
    ];
    const levelLabels = ['Mahadasha', 'Antardasha', 'Pratyantardasha', 'Sookshma', 'Prana'];

    return (
      <div className={`${level > 0 ? 'ml-4 mt-2' : ''}`}>
        <div 
          className={`${levelBgs[level % 5]} border-l-4 ${levelColors[level % 5]} rounded-r-lg p-3 cursor-pointer hover:bg-opacity-20 transition-all`}
          onClick={() => hasSubPeriods && togglePeriod(currentPath)}
        >
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div className="flex items-center gap-2">
              {hasSubPeriods && (
                <span className="text-gray-400">
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </span>
              )}
              <span className={`font-semibold ${getPlanetColor(lordName)}`}>
                {lordName}
              </span>
              <span className="text-gray-500 text-xs">{levelLabels[level] || `Level ${level}`}</span>
              {period.years && (
                <span className="text-gray-500 text-xs">({period.years?.toFixed(2)} yrs)</span>
              )}
            </div>
            <span className="text-gray-400 text-xs">
              {period.start_date || period.start || ''} → {period.end_date || period.end || ''}
            </span>
          </div>
        </div>
        
        {isExpanded && hasSubPeriods && (
          <div className="mt-1">
            {subPeriods.map((sub, idx) => (
              <DashaPeriod 
                key={`${currentPath}-${idx}`} 
                period={sub} 
                level={level + 1} 
                path={currentPath}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Get current dasha data - extract mahadashas array from nested structure
  const getCurrentDashaData = () => {
    // First check all_dashas (primary source)
    if (kernelData?.all_dashas?.[selectedDasha]) {
      const dashaObj = kernelData.all_dashas[selectedDasha];
      // If it has mahadashas array, return that
      if (dashaObj.mahadashas && Array.isArray(dashaObj.mahadashas)) {
        return dashaObj.mahadashas;
      }
      // If it's already an array, return it
      if (Array.isArray(dashaObj)) {
        return dashaObj;
      }
      // If it has periods, return that
      if (dashaObj.periods && Array.isArray(dashaObj.periods)) {
        return dashaObj.periods;
      }
    }
    
    // Fallback to main dasha structure
    if (selectedDasha === 'main' && kernelData?.dasha?.mahadashas) {
      return kernelData.dasha.mahadashas;
    }
    
    return [];
  };

  const dashaData = getCurrentDashaData();
  const currentDashaInfo = kernelData?.all_dashas?.[selectedDasha] || {};

  return (
    <div className="space-y-6">
      {/* Dasha System Selector */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Select Dasha System ({dashaSystems.length} available)</h3>
        <div className="flex flex-wrap gap-2">
          {dashaSystems.map(dasha => (
            <button
              key={dasha.key}
              onClick={() => setSelectedDasha(dasha.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedDasha === dasha.key
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {dasha.name}
            </button>
          ))}
        </div>
      </div>

      {/* Dasha Info */}
      {(currentDashaInfo.total_cycle || currentDashaInfo.dasha_at_birth || currentDashaInfo.balance) && (
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {currentDashaInfo.total_cycle && (
              <div>
                <p className="text-gray-400">Total Cycle</p>
                <p className="text-white font-medium">{currentDashaInfo.total_cycle} years</p>
              </div>
            )}
            {currentDashaInfo.balance && (
              <div>
                <p className="text-gray-400">Balance at Birth</p>
                <p className="text-white font-medium">
                  {typeof currentDashaInfo.balance === 'object' 
                    ? (currentDashaInfo.balance.formatted || `${currentDashaInfo.balance.total_years?.toFixed(2) || ''} years`)
                    : currentDashaInfo.balance}
                </p>
              </div>
            )}
            {currentDashaInfo.dasha_at_birth && (
              <div>
                <p className="text-gray-400">Dasha at Birth</p>
                <p className="text-amber-400 font-medium">{currentDashaInfo.dasha_at_birth}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dasha Periods */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">
          {selectedDasha.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} Dasha Periods
        </h3>
        <p className="text-gray-400 text-sm mb-4">Click on any period to expand sub-periods (up to Prana level - 5 levels deep)</p>
        
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {Array.isArray(dashaData) && dashaData.length > 0 && dashaData.map((period, idx) => (
            <DashaPeriod key={idx} period={period} />
          ))}
          {(!Array.isArray(dashaData) || dashaData.length === 0) && (
            <p className="text-gray-400">No dasha data available for this system</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ========== SHADBALA TAB ==========
const ShadbalaTab = ({ kernelData, getPlanetColor }) => {
  return (
    <div className="space-y-6">
      {/* Main Shadbala Table */}
      {kernelData?.shadbala && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Shadbala (Six-fold Strength)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-2">Planet</th>
                  <th className="text-right py-2 px-2">Sthana</th>
                  <th className="text-right py-2 px-2">Dig</th>
                  <th className="text-right py-2 px-2">Kala</th>
                  <th className="text-right py-2 px-2">Cheshta</th>
                  <th className="text-right py-2 px-2">Naisargika</th>
                  <th className="text-right py-2 px-2">Drik</th>
                  <th className="text-right py-2 px-2 font-bold">Total</th>
                  <th className="text-right py-2 px-2">Rupas</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kernelData.shadbala).map(([planet, data]) => (
                  <tr key={planet} className="border-b border-gray-800 hover:bg-gray-800/50">
                    <td className={`py-3 px-2 font-semibold ${getPlanetColor(planet)}`}>{planet}</td>
                    <td className="py-3 px-2 text-right text-gray-300">{data.sthana_bala?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-gray-300">{data.dig_bala?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-gray-300">{data.kala_bala?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-gray-300">{data.cheshta_bala?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-gray-300">{data.naisargika_bala?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-gray-300">{data.drik_bala?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-amber-400 font-bold">{data.total?.toFixed(2) || '-'}</td>
                    <td className="py-3 px-2 text-right text-green-400">{data.rupas?.toFixed(2) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Bhava Bala */}
      {kernelData?.bhava_bala && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Bhava Bala (House Strength)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-3">House</th>
                  <th className="text-right py-2 px-3">Strength</th>
                  <th className="text-right py-2 px-3">Rank</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kernelData.bhava_bala).map(([house, data]) => (
                  <tr key={house} className="border-b border-gray-800">
                    <td className="py-2 px-3 text-amber-400 font-semibold">House {house}</td>
                    <td className="py-2 px-3 text-right text-white">{typeof data === 'object' ? data.strength?.toFixed(2) : data?.toFixed?.(2) || data}</td>
                    <td className="py-2 px-3 text-right text-gray-400">{typeof data === 'object' ? data.rank : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Vimshopaka Bala */}
      {kernelData?.vimshopaka_bala && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Vimshopaka Bala</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-3">Planet</th>
                  <th className="text-right py-2 px-3">Shadvarga</th>
                  <th className="text-right py-2 px-3">Saptavarga</th>
                  <th className="text-right py-2 px-3">Dashavarga</th>
                  <th className="text-right py-2 px-3">Shodashavarga</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kernelData.vimshopaka_bala).map(([planet, data]) => (
                  <tr key={planet} className="border-b border-gray-800">
                    <td className={`py-2 px-3 font-semibold ${getPlanetColor(planet)}`}>{planet}</td>
                    <td className="py-2 px-3 text-right text-white">{data.shadvarga?.toFixed(2) || '-'}</td>
                    <td className="py-2 px-3 text-right text-white">{data.saptavarga?.toFixed(2) || '-'}</td>
                    <td className="py-2 px-3 text-right text-white">{data.dashavarga?.toFixed(2) || '-'}</td>
                    <td className="py-2 px-3 text-right text-amber-400">{data.shodashavarga?.toFixed(2) || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== ASHTAKAVARGA TAB ==========
const AshtakavargaTab = ({ kernelData }) => {
  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const signs = ['Ari', 'Tau', 'Gem', 'Can', 'Leo', 'Vir', 'Lib', 'Sco', 'Sag', 'Cap', 'Aqu', 'Pis'];

  return (
    <div className="space-y-6">
      {/* Sarvashtakavarga */}
      {kernelData?.ashtakavarga?.sarvashtakavarga && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Sarvashtakavarga (Total Points)</h3>
          <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
            {kernelData.ashtakavarga.sarvashtakavarga.map((points, idx) => (
              <div key={idx} className="bg-gray-900/50 rounded-lg p-3 text-center border border-gray-700">
                <p className="text-gray-400 text-xs">{signs[idx]}</p>
                <p className={`text-lg font-bold ${points >= 28 ? 'text-green-400' : points <= 22 ? 'text-red-400' : 'text-white'}`}>
                  {points}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Planet-wise Ashtakavarga */}
      {kernelData?.ashtakavarga?.planet_wise && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Planet-wise Ashtakavarga</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-2">Planet</th>
                  {signs.map(sign => (
                    <th key={sign} className="text-center py-2 px-1">{sign}</th>
                  ))}
                  <th className="text-center py-2 px-2 font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {planets.map(planet => {
                  const planetData = kernelData.ashtakavarga.planet_wise[planet];
                  if (!planetData) return null;
                  const total = Array.isArray(planetData) ? planetData.reduce((a, b) => a + b, 0) : 0;
                  return (
                    <tr key={planet} className="border-b border-gray-800">
                      <td className="py-2 px-2 text-amber-400 font-semibold">{planet}</td>
                      {Array.isArray(planetData) && planetData.map((points, idx) => (
                        <td key={idx} className={`py-2 px-1 text-center ${points >= 4 ? 'text-green-400' : points <= 2 ? 'text-red-400' : 'text-white'}`}>
                          {points}
                        </td>
                      ))}
                      <td className="py-2 px-2 text-center text-amber-400 font-bold">{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Extended Ashtakavarga */}
      {kernelData?.extended_ashtakavarga && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Extended Ashtakavarga Analysis</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-96">
            {JSON.stringify(kernelData.extended_ashtakavarga, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ========== JAIMINI TAB ==========
const JaiminiTab = ({ kernelData, getPlanetColor }) => {
  return (
    <div className="space-y-6">
      {/* Chara Karakas */}
      {kernelData?.jaimini?.chara_karakas && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Chara Karakas (Variable Significators)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(kernelData.jaimini.chara_karakas).map(([karaka, data]) => (
              <div key={karaka} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-amber-400 font-semibold text-sm">{data.karaka_full || karaka}</p>
                <p className={`text-xl font-bold mt-1 ${getPlanetColor(data.planet || data)}`}>
                  {data.planet || data}
                </p>
                {(data.degree || data.degree_decimal) && (
                  <p className="text-gray-400 text-sm">
                    {typeof data.degree === 'string' ? data.degree : 
                     typeof data.degree_decimal === 'number' ? `${data.degree_decimal.toFixed(2)}°` : 
                     data.degree}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Arudha Padas */}
      {kernelData?.jaimini?.arudha_padas && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Arudha Padas</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(kernelData.jaimini.arudha_padas).map(([pada, data]) => (
              <div key={pada} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-amber-400 font-semibold text-sm">{pada}</p>
                <p className="text-white text-lg">{data.sign || data}</p>
                {data.house && <p className="text-gray-400 text-sm">House {data.house}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Jaimini Aspects */}
      {kernelData?.jaimini?.rashi_aspects && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Jaimini Rashi Aspects</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-64">
            {JSON.stringify(kernelData.jaimini.rashi_aspects, null, 2)}
          </pre>
        </div>
      )}

      {/* Full Jaimini Data */}
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Complete Jaimini Data</h3>
        <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-96">
          {JSON.stringify(kernelData?.jaimini, null, 2)}
        </pre>
      </div>
    </div>
  );
};

// ========== KP SYSTEM TAB ==========
const KPSystemTab = ({ kernelData, getPlanetColor }) => {
  return (
    <div className="space-y-6">
      {/* KP Ayanamsha */}
      {kernelData?.kp_ayanamsha && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">KP Ayanamsha</h3>
          <p className="text-white text-2xl font-mono">{kernelData.kp_ayanamsha}°</p>
        </div>
      )}

      {/* KP Planet Data */}
      {kernelData?.kp_system && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">KP Planet Positions</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-2">Planet</th>
                  <th className="text-left py-2 px-2">Sign</th>
                  <th className="text-left py-2 px-2">Star Lord</th>
                  <th className="text-left py-2 px-2">Sub Lord</th>
                  <th className="text-left py-2 px-2">Sub-Sub</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kernelData.kp_system).map(([planet, data]) => (
                  <tr key={planet} className="border-b border-gray-800">
                    <td className={`py-2 px-2 font-semibold ${getPlanetColor(planet)}`}>{planet}</td>
                    <td className="py-2 px-2 text-white">{data.sign_full || data.sign}</td>
                    <td className="py-2 px-2 text-purple-300">{data.NL_full || data.NL || data.nakshatra_lord || '-'}</td>
                    <td className="py-2 px-2 text-cyan-300">{data.SL_full || data.SL || data.sub_lord || '-'}</td>
                    <td className="py-2 px-2 text-gray-400">{data.SS_full || data.SS || data.sub_sub_lord || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* KP Bhava (Placidus) */}
      {kernelData?.kp_bhava_placidus && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">KP Bhava Cusps (Placidus)</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 border-b border-gray-700">
                  <th className="text-left py-2 px-2">Cusp</th>
                  <th className="text-left py-2 px-2">Degree</th>
                  <th className="text-left py-2 px-2">Sign</th>
                  <th className="text-left py-2 px-2">Star Lord</th>
                  <th className="text-left py-2 px-2">Sub Lord</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kernelData.kp_bhava_placidus).map(([cusp, data]) => (
                  <tr key={cusp} className="border-b border-gray-800">
                    <td className="py-2 px-2 text-amber-400 font-semibold">{cusp}</td>
                    <td className="py-2 px-2 text-white font-mono">
                      {typeof data.degree === 'string' ? data.degree : 
                       typeof data.degree_decimal === 'number' ? `${data.degree_decimal.toFixed(2)}°` :
                       data.degree || data}
                    </td>
                    <td className="py-2 px-2 text-white">{data.sign || data.sign_full || '-'}</td>
                    <td className="py-2 px-2 text-purple-300">{data.NL_full || data.NL || data.star_lord || '-'}</td>
                    <td className="py-2 px-2 text-cyan-300">{data.SL_full || data.SL || data.sub_lord || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* KP House Significations */}
      {kernelData?.kp_house_significations && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">KP House Significations</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-64">
            {JSON.stringify(kernelData.kp_house_significations, null, 2)}
          </pre>
        </div>
      )}

      {/* KP Planet Significations */}
      {kernelData?.kp_planet_significations && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">KP Planet Significations</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-64">
            {JSON.stringify(kernelData.kp_planet_significations, null, 2)}
          </pre>
        </div>
      )}

      {/* Ruling Planets */}
      {kernelData?.ruling_planets && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Ruling Planets</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(kernelData.ruling_planets).map(([key, value]) => (
              <div key={key} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-400 text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                <p className={`text-lg font-semibold ${getPlanetColor(value)}`}>{value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== YOGAS TAB ==========
const YogasTab = ({ kernelData, getPlanetColor }) => {
  const yogas = kernelData?.yogas || [];

  return (
    <div className="space-y-6">
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Detected Yogas ({yogas.length})</h3>
        
        {yogas.length > 0 ? (
          <div className="space-y-4">
            {yogas.map((yoga, idx) => (
              <div key={idx} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <Star className="w-5 h-5 text-amber-400" />
                  <span className="text-amber-400 font-semibold text-lg">{yoga.name}</span>
                  {yoga.type && (
                    <span className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">{yoga.type}</span>
                  )}
                </div>
                {yoga.description && (
                  <p className="text-gray-300 text-sm mb-3">{yoga.description}</p>
                )}
                {yoga.planets && (
                  <div className="flex flex-wrap gap-2">
                    {yoga.planets.map(p => (
                      <span key={p} className={`text-sm ${getPlanetColor(p)} bg-gray-800 px-2 py-1 rounded`}>
                        {p}
                      </span>
                    ))}
                  </div>
                )}
                {yoga.houses && (
                  <div className="mt-2">
                    <span className="text-gray-400 text-sm">Houses: </span>
                    <span className="text-white text-sm">{yoga.houses.join(', ')}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">No yogas data available</p>
        )}
      </div>
    </div>
  );
};

// ========== UPAGRAHAS TAB ==========
const UpagrahasTab = ({ kernelData }) => {
  const upagrahas = kernelData?.upagrahas || {};

  return (
    <div className="space-y-6">
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Upagrahas (Sub-Planets)</h3>
        
        {Object.keys(upagrahas).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(upagrahas).map(([name, data]) => (
              <div key={name} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <h4 className="text-amber-400 font-semibold mb-2">{name}</h4>
                <div className="space-y-1 text-sm">
                  {typeof data === 'object' ? (
                    Object.entries(data).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                        <span className="text-white">{typeof value === 'number' ? value.toFixed(2) : value}</span>
                      </div>
                    ))
                  ) : (
                    <p className="text-white">{data}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-400">No upagrahas data available</p>
        )}
      </div>

      {/* Fortuna */}
      {kernelData?.fortuna && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Part of Fortune (Fortuna)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(kernelData.fortuna).map(([key, value]) => (
              <div key={key} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-400 text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                <p className="text-white text-lg">{typeof value === 'number' ? value.toFixed(2) : value}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== VARSHAPHALA TAB ==========
const VarshaphalaTab = ({ kernelData, getChartData, showDegrees, getPlanetColor }) => {
  const [selectedYear, setSelectedYear] = useState(0);
  const varshaphala = kernelData?.varshaphala || [];

  if (varshaphala.length === 0) {
    return (
      <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
        <p className="text-gray-400">No Varshaphala data available</p>
      </div>
    );
  }

  const currentYear = varshaphala[selectedYear];

  // Build chart data for selected year
  const getYearChartData = () => {
    if (!currentYear?.planets) return { planets: {}, houseSignMap: {}, ascendantSign: 'Leo' };
    
    const planets = {};
    Object.entries(currentYear.planets).forEach(([planet, data]) => {
      planets[planet] = {
        sign: data.sign,
        degree: data.degree || 0,
        house: data.house,
        retrograde: data.retrograde || false
      };
    });

    // Build house sign map from year ascendant
    const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
    const ascSign = currentYear.ascendant?.sign || 'Leo';
    const ascIndex = signs.indexOf(ascSign);
    const houseSignMap = {};
    for (let i = 0; i < 12; i++) {
      houseSignMap[i + 1] = signs[(ascIndex + i) % 12];
    }

    return { planets, houseSignMap, ascendantSign: ascSign };
  };

  const yearChartData = getYearChartData();

  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <h3 className="text-lg font-semibold text-amber-400 mb-4">Select Year</h3>
        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
          {varshaphala.map((year, idx) => (
            <button
              key={idx}
              onClick={() => setSelectedYear(idx)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                selectedYear === idx
                  ? 'bg-amber-500 text-black'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              Year {year.year || idx + 1} ({year.age || idx + 1} yrs)
            </button>
          ))}
        </div>
      </div>

      {/* Year Chart & Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Varshaphala Chart */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4 text-center">
            Varsha Kundali - Year {currentYear.year || selectedYear + 1}
          </h3>
          <div className="flex justify-center">
            <ChartToggle
              planets={yearChartData.planets}
              houseSignMap={yearChartData.houseSignMap}
              ascendantSign={yearChartData.ascendantSign}
              size={280}
              showDegrees={showDegrees}
            />
          </div>
        </div>

        {/* Year Info */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Year Details</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-400">Year</p>
                <p className="text-white font-medium">{currentYear.year}</p>
              </div>
              <div>
                <p className="text-gray-400">Age</p>
                <p className="text-white font-medium">{currentYear.age} years</p>
              </div>
              <div>
                <p className="text-gray-400">Muntha</p>
                <p className="text-white font-medium">{currentYear.muntha?.sign || currentYear.muntha}</p>
              </div>
              <div>
                <p className="text-gray-400">Year Lord</p>
                <p className={`font-medium ${getPlanetColor(currentYear.year_lord)}`}>{currentYear.year_lord}</p>
              </div>
            </div>

            {/* Planet Positions Table */}
            {currentYear.planets && (
              <div className="mt-4">
                <p className="text-gray-400 text-sm mb-2">Planet Positions</p>
                <div className="overflow-x-auto max-h-48">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-gray-400 border-b border-gray-700">
                        <th className="text-left py-1 px-2">Planet</th>
                        <th className="text-left py-1 px-2">Sign</th>
                        <th className="text-left py-1 px-2">Deg</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(currentYear.planets).map(([planet, data]) => (
                        <tr key={planet} className="border-b border-gray-800">
                          <td className={`py-1 px-2 ${getPlanetColor(planet)}`}>{planet}</td>
                          <td className="py-1 px-2 text-white">{data.sign}</td>
                          <td className="py-1 px-2 text-gray-300">{data.degree?.toFixed(1)}°</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ========== SPECIAL LAGNAS TAB ==========
const SpecialLagnasTab = ({ kernelData }) => {
  // Collect all special lagnas from various sources
  const specialLagnas = {};
  
  // From ascendant data
  if (kernelData?.ascendant) {
    Object.entries(kernelData.ascendant).forEach(([key, value]) => {
      if (key !== 'd1') {
        specialLagnas[key] = value;
      }
    });
  }

  // Bhava Madhya
  const bhavaMadhya = kernelData?.bhava_madhya || {};

  // Sudarshana Chakra
  const sudarshanaChakra = kernelData?.sudarshana_chakra || {};

  return (
    <div className="space-y-6">
      {/* Special Lagnas */}
      {Object.keys(specialLagnas).length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Special Lagnas</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {Object.entries(specialLagnas).map(([lagna, data]) => (
              <div key={lagna} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-amber-400 font-semibold text-sm uppercase">{lagna.replace(/_/g, ' ')}</p>
                <p className="text-white text-lg">{data.sign || data}</p>
                {data.degree !== undefined && (
                  <p className="text-gray-400 text-sm">{data.degree?.toFixed(2)}°</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bhava Madhya */}
      {Object.keys(bhavaMadhya).length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Bhava Madhya (House Mid-points)</h3>
          <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {Object.entries(bhavaMadhya).map(([house, data]) => (
              <div key={house} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 text-center">
                <p className="text-amber-400 font-semibold">H{house}</p>
                <p className="text-white">{typeof data === 'object' ? data.degree?.toFixed(2) : data?.toFixed?.(2) || data}°</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sudarshana Chakra */}
      {Object.keys(sudarshanaChakra).length > 0 && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Sudarshana Chakra</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-64">
            {JSON.stringify(sudarshanaChakra, null, 2)}
          </pre>
        </div>
      )}

      {/* Maitri Chakra */}
      {kernelData?.maitri_chakra && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Maitri Chakra (Friendship)</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-64">
            {JSON.stringify(kernelData.maitri_chakra, null, 2)}
          </pre>
        </div>
      )}

      {/* Tara Chakra */}
      {kernelData?.tara_chakra && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Tara Chakra</h3>
          <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 max-h-64">
            {JSON.stringify(kernelData.tara_chakra, null, 2)}
          </pre>
        </div>
      )}

      {/* Functional Nature */}
      {kernelData?.functional_nature && (
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-amber-400 mb-4">Functional Nature of Planets</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(kernelData.functional_nature).map(([planet, nature]) => (
              <div key={planet} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                <p className="text-amber-400 font-semibold">{planet}</p>
                <p className={`text-sm ${nature === 'Benefic' ? 'text-green-400' : nature === 'Malefic' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {nature}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ========== RAW JSON TAB ==========
const RawJSONTab = ({ kernelData, copyToClipboard, copiedField }) => {
  return (
    <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-amber-400">Complete Raw JSON Data</h3>
        <button
          onClick={() => copyToClipboard(kernelData, 'all')}
          className="text-sm text-gray-400 hover:text-white flex items-center gap-2 bg-gray-700 px-4 py-2 rounded-lg"
        >
          {copiedField === 'all' ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
          Copy All
        </button>
      </div>
      <pre className="bg-black rounded-lg p-4 overflow-auto text-xs text-gray-300 max-h-[70vh]">
        {JSON.stringify(kernelData, null, 2)}
      </pre>
    </div>
  );
};

export default MandeepKernelPage;
